<template>
  <div class="shopping-cart">
    <div class="cart-header">
      <h2>Your Cart</h2>
      <button v-if="cartItems.length" @click="clearCart" class="btn btn-text">Clear Cart</button>
    </div>
    
    <div v-if="!cartItems.length" class="empty-cart">
      <p>Your cart is empty</p>
      <button @click="$emit('close')" class="btn btn-primary">Continue Shopping</button>
    </div>
    
    <div v-else class="cart-content">
      <!-- Cart Items -->
      <div class="cart-items">
        <div v-for="item in cartItems" :key="item.uuid" class="cart-item">
          <div class="item-image">
            <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.name">
            <div v-else class="placeholder-image">No Image</div>
          </div>
          
          <div class="item-details">
            <h3>{{ item.name }}</h3>
            <p class="item-price">£{{ item.price.toFixed(2) }}</p>
          </div>
          
          <div class="item-quantity">
            <button 
              @click="updateQuantity(item.uuid, Math.max(1, item.quantity - 1))" 
              class="quantity-btn"
              :disabled="item.quantity <= 1"
            >-</button>
            <span>{{ item.quantity }}</span>
            <button 
              @click="updateQuantity(item.uuid, item.quantity + 1)" 
              class="quantity-btn"
            >+</button>
          </div>
          
          <div class="item-total">
            £{{ (item.price * item.quantity).toFixed(2) }}
          </div>
          
          <button @click="removeItem(item.uuid)" class="remove-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      
      <!-- Delivery Options -->
      <div class="delivery-options">
        <h3>Delivery Method</h3>
        <div class="delivery-methods">
          <div 
            class="delivery-method" 
            :class="{ 'selected': deliveryMethod === 'pickup' }"
            @click="setDeliveryMethod('pickup')"
          >
            <i class="fas fa-store"></i>
            <div>
              <h4>Store Pickup</h4>
              <p>Pick up your order at our store</p>
            </div>
          </div>
          
          <div 
            class="delivery-method" 
            :class="{ 'selected': deliveryMethod === 'delivery' }"
            @click="setDeliveryMethod('delivery')"
          >
            <i class="fas fa-truck"></i>
            <div>
              <h4>Home Delivery</h4>
              <p>Get your order delivered to your address</p>
            </div>
          </div>
        </div>
        
        <!-- Delivery Address (only shown when delivery is selected) -->
        <div v-if="deliveryMethod === 'delivery'" class="delivery-address">
          <h3>Delivery Address</h3>
          <div class="form-group">
            <label for="fullName">Full Name</label>
            <input 
              type="text" 
              id="fullName" 
              v-model="address.fullName" 
              placeholder="Enter your full name"
            >
          </div>
          
          <div class="form-group">
            <label for="addressLine1">Address Line 1</label>
            <input 
              type="text" 
              id="addressLine1" 
              v-model="address.line1" 
              placeholder="Street address"
            >
          </div>
          
          <div class="form-group">
            <label for="addressLine2">Address Line 2 (Optional)</label>
            <input 
              type="text" 
              id="addressLine2" 
              v-model="address.line2" 
              placeholder="Apartment, suite, etc."
            >
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="city">City</label>
              <input 
                type="text" 
                id="city" 
                v-model="address.city" 
                placeholder="City"
              >
            </div>
            
            <div class="form-group">
              <label for="postcode">Postcode</label>
              <input 
                type="text" 
                id="postcode" 
                v-model="address.postcode" 
                placeholder="Postcode"
              >
            </div>
          </div>
          
          <div class="form-group">
            <label for="phone">Phone Number</label>
            <input 
              type="tel" 
              id="phone" 
              v-model="address.phone" 
              placeholder="Phone number for delivery"
            >
          </div>
        </div>
      </div>
      
      <!-- Order Summary -->
      <div class="order-summary">
        <h3>Order Summary</h3>
        <div class="summary-row">
          <span>Subtotal</span>
          <span>£{{ cartTotal.toFixed(2) }}</span>
        </div>
        <div v-if="deliveryMethod === 'delivery'" class="summary-row">
          <span>Delivery Fee</span>
          <span>£{{ deliveryFee.toFixed(2) }}</span>
        </div>
        <div class="summary-row total">
          <span>Total</span>
          <span>£{{ orderTotal.toFixed(2) }}</span>
        </div>
        
        <!-- PayPal Button -->
        <div class="checkout-button">
          <div v-if="canCheckout" ref="paypalRef"></div>
          <button 
            v-else 
            class="btn btn-primary btn-block" 
            disabled
          >
            {{ checkoutButtonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { initPayPalButton } from '@/services/paypal'

export default {
  name: 'ShoppingCart',
  emits: ['close'],
  
  setup(props, { emit }) {
    const store = useStore()
    const paypalButton = ref(null)
    const paypalRef = ref(null)
    
    // Cart data from store
    const cartItems = computed(() => store.getters['cart/cartItems'])
    const cartTotal = computed(() => store.getters['cart/cartTotal'])
    const deliveryMethod = computed(() => store.getters['cart/deliveryMethod'])
    
    // Local state
    const address = ref({
      fullName: '',
      line1: '',
      line2: '',
      city: '',
      postcode: '',
      phone: ''
    })
    
    // Computed values
    const deliveryFee = computed(() => {
      return deliveryMethod.value === 'delivery' ? 5.00 : 0
    })
    
    const orderTotal = computed(() => {
      return cartTotal.value + deliveryFee.value
    })
    
    const isAddressValid = computed(() => {
      if (deliveryMethod.value === 'pickup') return true
      
      return (
        address.value.fullName.trim() !== '' &&
        address.value.line1.trim() !== '' &&
        address.value.city.trim() !== '' &&
        address.value.postcode.trim() !== '' &&
        address.value.phone.trim() !== ''
      )
    })
    
    const canCheckout = computed(() => {
      return cartItems.value.length > 0 && isAddressValid.value
    })
    
    const checkoutButtonText = computed(() => {
      if (cartItems.value.length === 0) {
        return 'Your cart is empty'
      }
      
      if (deliveryMethod.value === 'delivery' && !isAddressValid.value) {
        return 'Please complete delivery address'
      }
      
      return 'Proceed to Checkout'
    })
    
    // Methods
    const clearCart = () => {
      store.dispatch('cart/clearCart')
    }
    
    const removeItem = (uuid) => {
      store.dispatch('cart/removeCartItem', uuid)
    }
    
    const updateQuantity = (uuid, quantity) => {
      store.dispatch('cart/updateItemQuantity', { uuid, quantity })
    }
    
    const setDeliveryMethod = (method) => {
      store.dispatch('cart/setDeliveryMethod', method)
    }
    
    const initializePayPalButton = async () => {
      console.log('Initializing PayPal button in cart', {
        canCheckout: canCheckout.value,
        orderTotal: orderTotal.value,
        paypalRefExists: !!paypalRef.value
      })
      
      if (!canCheckout.value || !orderTotal.value) return
      
      if (paypalButton.value) {
        console.log('Closing existing PayPal button')
        paypalButton.value.close()
      }
      
      try {
        console.log('Creating new PayPal button with total:', orderTotal.value)
        paypalButton.value = await initPayPalButton(
          orderTotal.value,
          handlePaymentSuccess
        )
        
        if (paypalButton.value && paypalRef.value) {
          console.log('Rendering PayPal button to ref element')
          paypalButton.value.render(paypalRef.value)
        } else {
          console.error('Cannot render PayPal button', {
            buttonExists: !!paypalButton.value,
            refExists: !!paypalRef.value
          })
        }
      } catch (error) {
        console.error('Failed to initialize PayPal:', error)
      }
    }
    
    const handlePaymentSuccess = async (order) => {
      try {
        // Save delivery address if delivery method is selected
        if (deliveryMethod.value === 'delivery') {
          store.dispatch('cart/setDeliveryAddress', address.value)
        }
        
        // Save order to Firebase
        const result = await store.dispatch('cart/saveOrder', {
          orderId: order.id,
          paymentDetails: order
        })
        
        if (result.success) {
          // Show success message
          store.commit('SET_NOTIFICATION', {
            type: 'success',
            message: 'Your order has been placed successfully!'
          })
          
          // Close cart
          emit('close')
        } else {
          throw new Error('Failed to save order')
        }
      } catch (error) {
        console.error('Order processing error:', error)
        store.commit('SET_NOTIFICATION', {
          type: 'error',
          message: 'There was an error processing your order. Please try again.'
        })
      }
    }
    
    // Watch for changes that require PayPal button update
    watch([orderTotal, canCheckout], () => {
      initializePayPalButton()
    })
    
    // Initialize
    onMounted(() => {
      // Add a small delay to ensure the DOM is fully rendered
      setTimeout(() => {
        initializePayPalButton()
      }, 100)
    })
    
    return {
      cartItems,
      cartTotal,
      deliveryMethod,
      deliveryFee,
      orderTotal,
      address,
      canCheckout,
      checkoutButtonText,
      clearCart,
      removeItem,
      updateQuantity,
      setDeliveryMethod,
      paypalRef
    }
  }
}
</script>

<style lang="scss" scoped>
.shopping-cart {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  max-width: 900px;
  margin: 0 auto;
  
  .cart-header {
    padding: 1.5rem;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    h2 {
      margin: 0;
      color: #333;
    }
    
    .btn-text {
      background: none;
      border: none;
      color: #dc3545;
      cursor: pointer;
      font-size: 0.9rem;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  .empty-cart {
    padding: 3rem 1.5rem;
    text-align: center;
    
    p {
      color: #666;
      margin-bottom: 1.5rem;
      font-size: 1.1rem;
    }
  }
  
  .cart-content {
    padding: 1.5rem;
  }
  
  .cart-items {
    margin-bottom: 2rem;
    
    .cart-item {
      display: flex;
      align-items: center;
      padding: 1rem 0;
      border-bottom: 1px solid #eee;
      
      &:last-child {
        border-bottom: none;
      }
      
      .item-image {
        width: 80px;
        height: 80px;
        margin-right: 1rem;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 6px;
        }
        
        .placeholder-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f8f9fa;
          color: #6c757d;
          font-size: 0.8rem;
          border-radius: 6px;
        }
      }
      
      .item-details {
        flex: 1;
        
        h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }
        
        .item-price {
          color: #666;
          margin: 0;
        }
      }
      
      .item-quantity {
        display: flex;
        align-items: center;
        margin: 0 1.5rem;
        
        .quantity-btn {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 1px solid #ddd;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }
        
        span {
          margin: 0 0.8rem;
          min-width: 20px;
          text-align: center;
        }
      }
      
      .item-total {
        font-weight: bold;
        margin: 0 1.5rem;
        min-width: 70px;
        text-align: right;
      }
      
      .remove-btn {
        background: none;
        border: none;
        color: #999;
        cursor: pointer;
        font-size: 1rem;
        padding: 0.5rem;
        
        &:hover {
          color: #dc3545;
        }
      }
    }
  }
  
  .delivery-options {
    margin-bottom: 2rem;
    
    h3 {
      margin-top: 0;
      margin-bottom: 1rem;
      font-size: 1.2rem;
      color: #333;
    }
    
    .delivery-methods {
      display: flex;
      gap: 1rem;
      margin-bottom: 1.5rem;
      
      @media (max-width: 768px) {
        flex-direction: column;
      }
      
      .delivery-method {
        flex: 1;
        display: flex;
        align-items: center;
        padding: 1rem;
        border: 2px solid #eee;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          border-color: #ddd;
        }
        
        &.selected {
          border-color: #007bff;
          background-color: rgba(0, 123, 255, 0.05);
        }
        
        i {
          font-size: 1.5rem;
          margin-right: 1rem;
          color: #007bff;
        }
        
        h4 {
          margin: 0 0 0.3rem 0;
          font-size: 1rem;
        }
        
        p {
          margin: 0;
          font-size: 0.9rem;
          color: #666;
        }
      }
    }
    
    .delivery-address {
      background-color: #f9f9f9;
      padding: 1.5rem;
      border-radius: 8px;
      
      .form-group {
        margin-bottom: 1rem;
        
        label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #555;
        }
        
        input {
          width: 100%;
          padding: 0.8rem;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 1rem;
          
          &:focus {
            outline: none;
            border-color: #007bff;
          }
        }
      }
      
      .form-row {
        display: flex;
        gap: 1rem;
        
        .form-group {
          flex: 1;
        }
        
        @media (max-width: 576px) {
          flex-direction: column;
          gap: 0;
        }
      }
    }
  }
  
  .order-summary {
    background-color: #f9f9f9;
    padding: 1.5rem;
    border-radius: 8px;
    
    h3 {
      margin-top: 0;
      margin-bottom: 1.5rem;
      font-size: 1.2rem;
      color: #333;
    }
    
    .summary-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 1rem;
      
      &.total {
        margin-top: 1.5rem;
        padding-top: 1rem;
        border-top: 1px solid #ddd;
        font-weight: bold;
        font-size: 1.1rem;
      }
    }
    
    .checkout-button {
      margin-top: 1.5rem;
      
      .btn-block {
        width: 100%;
        padding: 1rem;
        font-size: 1.1rem;
      }
    }
  }
  
  @media (max-width: 768px) {
    .cart-item {
      flex-wrap: wrap;
      
      .item-details {
        width: calc(100% - 100px);
      }
      
      .item-quantity, .item-total {
        margin-top: 1rem;
      }
      
      .item-quantity {
        order: 3;
        margin-left: 90px;
      }
      
      .item-total {
        order: 4;
        flex: 1;
        text-align: right;
      }
      
      .remove-btn {
        order: 5;
      }
    }
  }
}
</style> 